import React from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  Building2, 
  Mail, 
  Phone, 
  MapPin, 
  Users, 
  Shield, 
  Clock, 
  Calendar,
  Target,
  Edit,
  CheckCircle,
  Activity,
  TrendingUp,
  Settings
} from 'lucide-react';

const ViewDepartmentModal = ({ isOpen, onClose, department, onEdit }) => {
  if (!department) return null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-50 text-green-700 border-green-200';
      case 'inactive': return 'bg-gray-50 text-gray-700 border-gray-200';
      case 'maintenance': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getDepartmentTypeIcon = (type) => {
    switch (type) {
      case 'technical': return <Activity className="h-6 w-6 text-blue-600" />;
      case 'customer-service': return <Users className="h-6 w-6 text-blue-600" />;
      case 'sales': return <Target className="h-6 w-6 text-blue-600" />;
      case 'management': return <Shield className="h-6 w-6 text-blue-600" />;
      default: return <Building2 className="h-6 w-6 text-blue-600" />;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Department Details"
      size="xl"
    >
      <div className="flex flex-col h-full max-h-[calc(90vh-120px)]">
        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <div className="space-y-6">
            {/* Department Header */}
            <div className="flex items-start gap-4 pb-4 border-b border-gray-200">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center shadow-sm">
                {getDepartmentTypeIcon(department.type)}
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-1">{department.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{department.description}</p>
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={`${getStatusColor(department.status)} text-xs font-medium`}>
                    {department.status}
                  </Badge>
                  <span className="text-xs text-gray-500 font-medium">Type: {department.type || 'General'}</span>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onEdit(department);
                  onClose();
                }}
                className="flex items-center gap-2 border-amber-200 text-amber-600 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-700"
              >
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                <Mail className="h-5 w-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900 text-lg">Contact Information</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Users className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 font-medium mb-1">Manager</p>
                    <p className="text-sm font-medium text-gray-900">{department.manager?.name || department.manager || 'Not assigned'}</p>
                  </div>
                </div>
                {department.email && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium mb-1">Email Address</p>
                      <p className="text-sm font-medium text-gray-900 break-words">{department.email}</p>
                    </div>
                  </div>
                )}
                {department.phone && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <Phone className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium mb-1">Phone Number</p>
                      <p className="text-sm font-medium text-gray-900">{department.phone}</p>
                    </div>
                  </div>
                )}
                {department.location && (
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <MapPin className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 font-medium mb-1">Location</p>
                      <p className="text-sm font-medium text-gray-900">{department.location}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Department Settings */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                <Settings className="h-5 w-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900 text-lg">Department Settings</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <Users className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 font-medium mb-1">Max Members</p>
                    <p className="text-sm font-medium text-gray-900">{department.maxMembers || 50}</p>
                  </div>
                </div>
                {department.workingHours && (
                  <>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Clock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 font-medium mb-1">Working Hours</p>
                        <p className="text-sm font-medium text-gray-900">
                          {department.workingHours.start || '09:00'} - {department.workingHours.end || '17:00'}
                        </p>
                      </div>
                    </div>
                    {department.workingHours.days && (
                      <div className="col-span-2 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 font-medium mb-2">Working Days</p>
                        <div className="flex flex-wrap gap-2">
                          {department.workingHours.days.map((day, index) => (
                            <Badge key={index} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 px-2 py-0.5 text-xs">
                              {day}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
                {department.sla && (
                  <>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <Clock className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 font-medium mb-1">Response Time</p>
                        <p className="text-sm font-medium text-gray-900">{department.sla.responseTime || 4} hours</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 font-medium mb-1">Resolution Time</p>
                        <p className="text-sm font-medium text-gray-900">{department.sla.resolutionTime || 24} hours</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Statistics */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                <TrendingUp className="h-5 w-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900 text-lg">Statistics</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <p className="text-xs text-gray-600 font-medium">Total Members</p>
                  </div>
                  <p className="text-2xl font-bold text-blue-900">{department.memberCount || department.userCount || 0}</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="h-4 w-4 text-green-600" />
                    <p className="text-xs text-gray-600 font-medium">Active Tickets</p>
                  </div>
                  <p className="text-2xl font-bold text-green-900">{department.ticketCount || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Footer Actions - Fixed at Bottom */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <Button 
            variant="outline" 
            onClick={onClose}
            className="px-6"
          >
            Close
          </Button>
          <Button
            onClick={() => {
              onEdit(department);
              onClose();
            }}
            className="flex items-center gap-2 px-6 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Edit className="h-4 w-4" />
            Edit Department
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ViewDepartmentModal;

